import React, { useRef, useState } from 'react';

import {
  IconButton,
  Tooltip,
  SelectChangeEvent,
  FormControl,
  Checkbox,
} from '@mui/material';
import {
  StyledSelect,
  StyledMenuItem,
  StyledLabel,
} from './StatusSelect.styles';
import { IconTextFieldWrapper } from 'molecules/IconTextField/IconTextField.styles';
import { palette } from 'utils/styles/variables';

import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

export const invoiceTypeOptions = [
  { key: 'LF', value: 'LF' },
  { key: 'GCP', value: 'GCP' },
  { key: 'SD', value: 'SD' },
  { key: 'GWS', value: 'GWS' },
  { key: 'CO', value: 'CO' },
];

interface InvoiceTypeSelectProps {
  /** receives the selected types as a comma-separated string ('' when none) */
  onChange?: (value: string) => void;
  options?: { key: string; value: string }[];
}

const InvoiceTypeSelect = ({
  onChange,
  options = invoiceTypeOptions,
}: InvoiceTypeSelectProps) => {
  const boxRef = useRef(null);

  const [open, setOpen] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const handleSelect = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;
    const next =
      typeof value === 'string'
        ? value.split(',').filter(Boolean)
        : (value as string[]);

    setTypeFilter(next);
    if (onChange) {
      onChange(next.join(','));
    }
  };

  const handleClickIcon = () => {
    setTimeout(() => {
      setOpen((isOpen) => !isOpen);
    }, 100);
  };

  const handleClear = () => {
    setOpen(false);
    setTypeFilter([]);

    if (onChange) onChange('');
  };

  return (
    <IconTextFieldWrapper ref={boxRef}>
      <Tooltip title="Filter by type">
        <IconButton
          aria-label="filter-type"
          onClick={handleClickIcon}
          sx={{
            p: '0 !important',
            width: '20px !important',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          disabled={open}
        >
          <FilterIcon color={palette.primary.main} />
        </IconButton>
      </Tooltip>

      <FormControl
        variant="standard"
        sx={{
          width: 160,
          minWidth: 160,
          display: open || typeFilter.length ? 'flex' : 'none',
        }}
        size="small"
      >
        <StyledLabel
          id="type-filter-label"
          shrink={false}
          sx={{ transform: 'translate(45%, 17px) !important' }}
        >
          {typeFilter.length ? '' : 'Select type'}
        </StyledLabel>
        <StyledSelect
          multiple
          open={open}
          value={typeFilter}
          id="type-filter-label"
          labelId="type-filter-label"
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={handleSelect}
          renderValue={(selected) => (selected as string[]).join(', ')}
          MenuProps={{
            className: 'invoice-type-select',
            sx: {
              '.MuiPaper-root': {
                width: 160,
                minHeight: `${options.length * 36 + 5}px`,
              },
            },
          }}
          IconComponent={() =>
            typeFilter.length ? (
              <IconButton
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  handleClear();
                }}
              >
                <CloseIcon color={palette.error.main} width={16} height={16} />
              </IconButton>
            ) : null
          }
        >
          {options.map(({ key, value }) => (
            <StyledMenuItem key={key} value={value}>
              <Checkbox
                size="small"
                checked={typeFilter.indexOf(value) > -1}
                sx={{ p: 0, mr: 1 }}
              />
              {key}
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </FormControl>
    </IconTextFieldWrapper>
  );
};

export default InvoiceTypeSelect;
